import { Box, Button, IconButton, Typography } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useEffect, useRef } from 'react';

// Mensagens amigáveis para falhas ao abrir a câmera (iOS/Android).
export function cameraErrorMessage(e) {
    const name = e && e.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') {
        return 'Permissão da câmera negada. Libere o acesso à câmera nos Ajustes do aparelho/navegador e tente novamente.';
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        return 'Nenhuma câmera encontrada neste aparelho.';
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
        return 'A câmera está em uso por outro aplicativo. Feche-o e tente novamente.';
    }
    if (name === 'OverconstrainedError') {
        return 'Não foi possível usar a câmera traseira deste aparelho.';
    }
    return 'Erro ao escanear código de barras: não foi possível acessar a câmera';
}

// Leitor de código de barras por câmera em tela cheia. Usa a API nativa
// BarcodeDetector quando existe (Android/Chrome); senão, cai para o
// @zxing/browser (iOS Safari), carregado sob demanda.
// Modo `continuous`: a câmera não fecha após a leitura; `onDetected` pode devolver
// uma Promise e novas leituras só são aceitas depois que ela resolve. O mesmo
// código em sequência imediata é ignorado por `cooldownMs`. `feedback` (nó React
// opcional) é exibido sobre o vídeo; `closeLabel` é o texto do botão de encerrar.
export default function BarcodeScanner({ onClose, onDetected, onError, continuous = false, cooldownMs = 2000, feedback = null, closeLabel = 'Concluir' }) {
    const videoRef = useRef(null);
    const onDetectedRef = useRef(onDetected);
    onDetectedRef.current = onDetected;

    useEffect(() => {
        let stream;
        let timer;
        let controls;
        let stopped = false;
        let done = false;

        const cleanup = () => {
            stopped = true;
            clearInterval(timer);
            if (controls) {
                try { controls.stop(); } catch (_) { /* já parado */ }
                controls = null;
            }
            if (stream) {
                stream.getTracks().forEach((t) => t.stop());
                stream = null;
            }
            const video = videoRef.current;
            if (video && video.srcObject) {
                try { video.srcObject.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
                video.srcObject = null;
            }
        };

        let busy = false;
        let lastCode = null;
        let lastAt = 0;

        const handleContinuous = async (value) => {
            if (busy || stopped || !value) return;
            if (value === lastCode && Date.now() - lastAt < cooldownMs) return;
            busy = true;
            try { if (navigator.vibrate) navigator.vibrate(80); } catch (_) { /* ignore */ }
            try {
                await onDetectedRef.current(value);
            } finally {
                lastCode = value;
                lastAt = Date.now();
                busy = false;
            }
        };

        const handleCode = (value) => {
            if (continuous) {
                handleContinuous(value);
                return;
            }
            if (done || stopped || !value) return;
            done = true;
            cleanup();
            try { if (navigator.vibrate) navigator.vibrate(80); } catch (_) { /* ignore */ }
            onDetected(value);
        };

        const openCamera = async () => {
            try {
                return await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
            } catch (e) {
                if (e && e.name === 'OverconstrainedError') {
                    return navigator.mediaDevices.getUserMedia({ video: true, audio: false });
                }
                throw e;
            }
        };

        const startNative = async () => {
            stream = await openCamera();
            if (stopped) {
                cleanup();
                return;
            }
            const video = videoRef.current;
            video.setAttribute('playsinline', '');
            video.muted = true;
            video.srcObject = stream;
            await video.play();
            const detector = new window.BarcodeDetector();
            timer = setInterval(async () => {
                try {
                    const codes = await detector.detect(video);
                    if (codes.length && codes[0].rawValue) handleCode(codes[0].rawValue);
                } catch (_) {
                    // frame sem leitura: tenta de novo no próximo ciclo
                }
            }, 300);
        };

        const startZxing = async () => {
            const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
                import('@zxing/browser'),
                import('@zxing/library')
            ]);
            if (stopped) return;
            const hints = new Map();
            hints.set(DecodeHintType.POSSIBLE_FORMATS, [
                BarcodeFormat.EAN_13,
                BarcodeFormat.EAN_8,
                BarcodeFormat.UPC_A,
                BarcodeFormat.UPC_E,
                BarcodeFormat.CODE_128,
                BarcodeFormat.CODE_39,
                BarcodeFormat.ITF,
                BarcodeFormat.QR_CODE
            ]);
            hints.set(DecodeHintType.TRY_HARDER, true);
            const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 150 });
            const video = videoRef.current;
            video.setAttribute('playsinline', '');
            video.muted = true;
            const onResult = (result) => {
                if (result) handleCode(result.getText());
            };
            let c;
            try {
                c = await reader.decodeFromConstraints({ video: { facingMode: { ideal: 'environment' } }, audio: false }, video, onResult);
            } catch (e) {
                if (e && e.name === 'OverconstrainedError' && !stopped) {
                    c = await reader.decodeFromConstraints({ video: true, audio: false }, video, onResult);
                } else {
                    throw e;
                }
            }
            if (stopped) {
                c.stop();
                return;
            }
            controls = c;
        };

        const start = async () => {
            try {
                if (typeof window.BarcodeDetector !== 'undefined') await startNative();
                else await startZxing();
            } catch (e) {
                if (stopped) return;
                cleanup();
                onError(cameraErrorMessage(e));
                onClose();
            }
        };
        start();
        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, backgroundColor: '#000', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ height: 'calc(56px + env(safe-area-inset-top, 0px))', pt: 'env(safe-area-inset-top, 0px)', boxSizing: 'border-box', flexShrink: 0, display: 'flex', alignItems: 'center', pl: 'calc(4px + env(safe-area-inset-left, 0px))', pr: 'calc(4px + env(safe-area-inset-right, 0px))', backgroundColor: '#000', color: '#fff' }}>
                <IconButton aria-label="Voltar" onClick={onClose} sx={{ color: '#fff' }}>
                    <ArrowBack />
                </IconButton>
                <Typography noWrap sx={{ flex: 1, ml: 1, fontSize: 20, fontWeight: 500, color: '#fff' }}>
                    Escanear Código de Barras
                </Typography>
            </Box>
            <Box component="video" ref={videoRef} playsInline muted autoPlay sx={{ flex: 1, minHeight: 0, width: '100%', objectFit: 'cover' }} />
            {continuous && (
                <Box sx={{ flexShrink: 0, backgroundColor: '#000', color: '#fff', px: 2, pt: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Box sx={{ minHeight: 56 }}>
                        {feedback || (
                            <Typography sx={{ fontSize: 14, opacity: 0.7 }}>Aponte a câmera para o código de barras do produto.</Typography>
                        )}
                    </Box>
                    <Button variant="contained" fullWidth onClick={onClose} sx={{ height: 44, textTransform: 'none', fontWeight: 700 }}>
                        {closeLabel}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
